import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { useI18n } from "../../lib/i18n";
import { ProductPhotos } from "./ProductPhotos";

export const Field = ({ label, children }) => (
  <div className="space-y-1">
    <p className="text-xs font-medium text-muted-foreground">{label}</p>
    {children}
  </div>
);

const numeric = (v) => v.replace(/[^0-9]/g, "");

/** Dialog tambah/ubah produk beserta foto, harga, stok, dan teks bahasa Jepang. */
export const ProductDialog = ({
  open,
  setOpen,
  form,
  setForm,
  editing,
  isOwner,
  suggest,
  onSave,
  onSaved,
}) => {
  const { t } = useI18n();
  const set = (patch) => setForm({ ...form, ...patch });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="product-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {editing ? t("edit_product") : t("new_product")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Nama produk">
            <Input
              data-testid="product-name-input"
              placeholder={t("name")}
              list="stok-name-options"
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
            />
          </Field>
          <datalist id="stok-name-options">
            {suggest("name").map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
          <Field label="Jenis (toko / resto)">
            <Select value={form.kind} onValueChange={(v) => set({ kind: v })}>
              <SelectTrigger data-testid="product-kind-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="grocery">{t("tab_grocery")}</SelectItem>
                <SelectItem value="menu">{t("tab_menu")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Kategori">
            <Input
              data-testid="product-category-input"
              placeholder={t("category")}
              list="stok-category-options"
              value={form.category}
              onChange={(e) => set({ category: e.target.value })}
            />
          </Field>
          <datalist id="stok-category-options">
            {suggest("category", form.kind).map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
          <p className="text-xs text-muted-foreground">{t("or_pick_category")}</p>
          <select
            data-testid="product-category-select"
            className="w-full h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm"
            value={suggest("category", form.kind).includes(form.category) ? form.category : ""}
            onChange={(e) => set({ category: e.target.value })}
          >
            <option value="">{t("new_category")}</option>
            {suggest("category", form.kind).map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">{t("category_hint")}</p>
          <Field label="Barcode">
            <Input
              data-testid="product-barcode-input"
              placeholder="Barcode (scan atau ketik)"
              value={form.barcode}
              onChange={(e) => set({ barcode: e.target.value.trim() })}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Harga jual (¥)">
              <Input
                data-testid="product-price-input"
                placeholder="Harga (¥)"
                inputMode="numeric"
                value={form.price}
                onChange={(e) => set({ price: numeric(e.target.value) })}
              />
            </Field>
            <Field label="Satuan (pcs / kg / pack)">
              <Input
                data-testid="product-unit-input"
                placeholder={t("unit") || "Satuan"}
                list="stok-unit-options"
                value={form.unit}
                onChange={(e) => set({ unit: e.target.value })}
              />
            </Field>
            <datalist id="stok-unit-options">
              {suggest("unit").map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </div>
          <Field label="Deskripsi (Indonesia)">
            <textarea
              data-testid="product-description-input"
              rows={3}
              placeholder="Deskripsi singkat produk (tampil di toko online)"
              value={form.description || ""}
              onChange={(e) => set({ description: e.target.value })}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Nama produk (bahasa Jepang)">
            <textarea
              data-testid="product-name-ja-input"
              rows={1}
              placeholder="商品名（日本語) — nama produk bahasa Jepang"
              value={form.name_ja || ""}
              onChange={(e) => set({ name_ja: e.target.value })}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Deskripsi (bahasa Jepang)">
            <textarea
              data-testid="product-description-ja-input"
              rows={2}
              placeholder="説明（日本語) — deskripsi bahasa Jepang"
              value={form.description_ja || ""}
              onChange={(e) => set({ description_ja: e.target.value })}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
            />
          </Field>
          <label className="flex items-center justify-between gap-3 text-sm rounded-xl bg-gray-50 px-3 py-2.5">
            <span>
              Tandai sebagai produk <b>NEW</b> di toko online
            </span>
            <Switch
              data-testid="product-new-switch"
              checked={!!form.is_new}
              onCheckedChange={(v) => set({ is_new: v })}
            />
          </label>
          {!/bento/i.test(form.category) && (
            <>
              <Field label="Berat / isi (mis. 500g, 1L)">
                <Input
                  data-testid="product-weight-input"
                  placeholder={t("weight_ph")}
                  list="stok-weight-options"
                  value={form.weight}
                  onChange={(e) => set({ weight: e.target.value })}
                />
              </Field>
              <datalist id="stok-weight-options">
                {suggest("weight").map((v) => (
                  <option key={v} value={v} />
                ))}
              </datalist>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            {isOwner && (
              <Field
                label={form.kind === "grocery" ? "Harga beli / modal (¥)" : "Biaya bahan (¥)"}
              >
                <Input
                  data-testid="product-cost-input"
                  placeholder={form.kind === "grocery" ? "Harga beli (¥)" : "Biaya bahan (¥)"}
                  inputMode="numeric"
                  value={form.cost}
                  onChange={(e) => set({ cost: numeric(e.target.value) })}
                />
              </Field>
            )}
            <Field label="Stok saat ini">
              <Input
                data-testid="product-stock-input"
                placeholder={t("stock")}
                inputMode="numeric"
                value={form.stock}
                onChange={(e) => set({ stock: numeric(e.target.value) })}
              />
            </Field>
            <Field label="Batas stok menipis">
              <Input
                data-testid="product-threshold-input"
                placeholder={t("min_threshold")}
                inputMode="numeric"
                value={form.low_stock_threshold}
                onChange={(e) => set({ low_stock_threshold: numeric(e.target.value) })}
              />
            </Field>
          </div>
          {form.kind === "menu" && (
            <Field label="Porsi tersedia per hari">
              <Input
                data-testid="product-daily-stock-input"
                placeholder={t("daily_stock")}
                inputMode="numeric"
                value={form.daily_stock}
                onChange={(e) => set({ daily_stock: numeric(e.target.value) })}
              />
            </Field>
          )}
          <ProductPhotos
            form={form}
            setForm={setForm}
            editing={editing}
            onSaved={onSaved}
          />
          <Input
            data-testid="product-image-input"
            placeholder={t("or_paste_url")}
            value={form.image_url}
            onChange={(e) => {
              const url = e.target.value.trim();
              setForm((f) => {
                const list = [...(f.images || [])];
                if (list.length === 0) list.push(url);
                else list[0] = url;
                return { ...f, image_url: url, images: list.filter(Boolean) };
              });
            }}
          />
          <Button className="w-full h-12" data-testid="save-product-btn" onClick={onSave}>
            {t("save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
